import axios from "axios";
import {
  // ================== AUTH ==================
  REGISTER_USER_REQUEST, REGISTER_USER_SUCCESS, REGISTER_USER_FAIL,
  LOGIN_USER_REQUEST, LOGIN_USER_SUCCESS, LOGIN_USER_FAIL,
  LOGOUT_USER_REQUEST, LOGOUT_USER_SUCCESS, LOGOUT_USER_FAIL,
  // ================== PASSWORD ==================
  FORGOT_PASSWORD_REQUEST, FORGOT_PASSWORD_SUCCESS, FORGOT_PASSWORD_FAIL,
  RESET_PASSWORD_REQUEST, RESET_PASSWORD_SUCCESS, RESET_PASSWORD_FAIL,
  UPDATE_PASSWORD_REQUEST, UPDATE_PASSWORD_SUCCESS, UPDATE_PASSWORD_FAIL,
  // ================== PROFILE ==================
  LOAD_USER_REQUEST, LOAD_USER_SUCCESS, LOAD_USER_FAIL,
  UPDATE_PROFILE_REQUEST, UPDATE_PROFILE_SUCCESS, UPDATE_PROFILE_FAIL,
  GET_USER_ACTIVITY_REQUEST, GET_USER_ACTIVITY_SUCCESS, GET_USER_ACTIVITY_FAIL,
  // ================== ADMIN USER MGMT ==================
  ALL_USERS_REQUEST, ALL_USERS_SUCCESS, ALL_USERS_FAIL,
  USERS_BY_FRANCHISE_REQUEST, USERS_BY_FRANCHISE_SUCCESS, USERS_BY_FRANCHISE_FAIL,
  CREATE_USER_REQUEST, CREATE_USER_SUCCESS, CREATE_USER_FAIL,
  EDIT_USER_REQUEST, EDIT_USER_SUCCESS, EDIT_USER_FAIL,
  DELETE_USER_REQUEST, DELETE_USER_SUCCESS, DELETE_USER_FAIL,
  CHANGE_USER_ROLE_REQUEST, CHANGE_USER_ROLE_SUCCESS, CHANGE_USER_ROLE_FAIL,
  TOGGLE_USER_STATUS_REQUEST, TOGGLE_USER_STATUS_SUCCESS, TOGGLE_USER_STATUS_FAIL,
  // ================== FRANCHISE ==================
  CREATE_FRANCHISE_REQUEST, CREATE_FRANCHISE_SUCCESS, CREATE_FRANCHISE_FAIL,
  GET_ALL_FRANCHISES_REQUEST, GET_ALL_FRANCHISES_SUCCESS, GET_ALL_FRANCHISES_FAIL,
  SELECT_FRANCHISE_REQUEST, SELECT_FRANCHISE_SUCCESS, SELECT_FRANCHISE_FAIL,
  SET_FRANCHISE_CONSOLE_REQUEST, SET_FRANCHISE_CONSOLE_SUCCESS, SET_FRANCHISE_CONSOLE_FAIL,
  GET_ACCESSIBLE_FRANCHISES_REQUEST, GET_ACCESSIBLE_FRANCHISES_SUCCESS, GET_ACCESSIBLE_FRANCHISES_FAIL,
  // ================== FRANCHISE STOCK/SALES ==================
  GET_FRANCHISE_STOCK_REQUEST, GET_FRANCHISE_STOCK_SUCCESS, GET_FRANCHISE_STOCK_FAIL,
  GET_FRANCHISE_SALES_REQUEST, GET_FRANCHISE_SALES_SUCCESS, GET_FRANCHISE_SALES_FAIL,
  // ================== UTILS ==================
  CLEAR_ERRORS
} from "../constants/userConstants";

// ========== Helpers ==========
const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}` : "/api/v1";
const getToken = () => localStorage.getItem("accessToken");
const getAuthConfig = () => ({
  headers: {
    Authorization: getToken() ? `Bearer ${getToken()}` : "",
    "Content-Type": "application/json",
  },
});

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "An error occurred";

// ================== AUTH ==================

// REGISTER
export const registerUser = (userData) => async (dispatch) => {
  try {
    dispatch({ type: REGISTER_USER_REQUEST });
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await axios.post(`${API_URL}/register`, userData, config);

    // Token set on registration
    if (data.accessToken) {
      localStorage.setItem("accessToken", data.accessToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.accessToken}`;
    }
    dispatch({ type: REGISTER_USER_SUCCESS, payload: data.user || data.data });
  } catch (error) {
    const msg = getErrorMessage(error);
    dispatch({ type: REGISTER_USER_FAIL, payload: msg });
    throw new Error(msg);
  }
};

// LOGIN
export const loginUser = (email, password, selectedFranchise, selectedRole) => async (dispatch) => {
  try {
    dispatch({ type: LOGIN_USER_REQUEST });
    const payload = { email, password, selectedFranchise, selectedRole };
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await axios.post(`${API_URL}/login`, payload, config);

    // Token set on login
    if (data.accessToken) {
      localStorage.setItem("accessToken", data.accessToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.accessToken}`;
    }
    dispatch({ type: LOGIN_USER_SUCCESS, payload: data.user || data.data });
  } catch (error) {
    const msg = getErrorMessage(error);
    dispatch({ type: LOGIN_USER_FAIL, payload: msg });
    throw new Error(msg);
  }
};

// LOAD USER
export const loadUser = () => async (dispatch) => {
  try {
    dispatch({ type: LOAD_USER_REQUEST });

    const config = getAuthConfig();
    const { data } = await axios.get(`${API_URL}/me`, config);

    // Use data.data (not data.user) because your backend responds with { success, data }
    dispatch({ type: LOAD_USER_SUCCESS, payload: data.data });
  } catch (error) {
    dispatch({ type: LOAD_USER_FAIL, payload: getErrorMessage(error) });
  }
};

// LOGOUT
export const logoutUser = () => async (dispatch) => {
  try {
    dispatch({ type: LOGOUT_USER_REQUEST });
    // You may use POST/GET depending on backend
    await axios.post(`${API_URL}/logout`, {}, getAuthConfig());
    localStorage.removeItem("accessToken");
    delete axios.defaults.headers.common["Authorization"];
    dispatch({ type: LOGOUT_USER_SUCCESS });
  } catch (error) {
    dispatch({ type: LOGOUT_USER_FAIL, payload: getErrorMessage(error) });
  }
};

// ================== PASSWORD ==================

// FORGOT PASSWORD
export const forgotPassword = (email) => async (dispatch) => {
  try {
    dispatch({ type: FORGOT_PASSWORD_REQUEST });
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await axios.post(`${API_URL}/forgotPassword`, { email }, config);
    dispatch({ type: FORGOT_PASSWORD_SUCCESS, payload: data.message });
  } catch (error) {
    dispatch({ type: FORGOT_PASSWORD_FAIL, payload: getErrorMessage(error) });
  }
};

// RESET PASSWORD
export const resetPassword = (token, passwords) => async (dispatch) => {
  try {
    dispatch({ type: RESET_PASSWORD_REQUEST });
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await axios.post(`${API_URL}/resetPassword/${token}`, passwords, config);
    dispatch({ type: RESET_PASSWORD_SUCCESS, payload: data.message || data.success });
  } catch (error) {
    dispatch({ type: RESET_PASSWORD_FAIL, payload: getErrorMessage(error) });
  }
};

// UPDATE PASSWORD
export const updateUserPassword = (currentPassword, newPassword) => async (dispatch) => {
  try {
    dispatch({ type: UPDATE_PASSWORD_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.put(
      `${API_URL}/updatePassword`,
      { currentPassword, newPassword },
      config
    );
    dispatch({ type: UPDATE_PASSWORD_SUCCESS, payload: data.message });
  } catch (error) {
    dispatch({ type: UPDATE_PASSWORD_FAIL, payload: getErrorMessage(error) });
  }
};

// ================== PROFILE ==================

// UPDATE PROFILE
export const updateProfile = (profileData) => async (dispatch) => {
  try {
    dispatch({ type: UPDATE_PROFILE_REQUEST });
    const isForm = profileData instanceof FormData;
    const config = getAuthConfig(isForm ? "multipart/form-data" : "application/json");
    const { data } = await axios.put(`${API_URL}/updateMyProfile`, profileData, config);
    dispatch({ type: UPDATE_PROFILE_SUCCESS, payload: data.message });
  } catch (error) {
    dispatch({ type: UPDATE_PROFILE_FAIL, payload: getErrorMessage(error) });
  }
};

// GET USER ACTIVITY
export const getUserActivity = () => async (dispatch) => {
  try {
    dispatch({ type: GET_USER_ACTIVITY_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.get(`${API_URL}/activity`, config);
    dispatch({ type: GET_USER_ACTIVITY_SUCCESS, payload: data.activities });
  } catch (error) {
    dispatch({ type: GET_USER_ACTIVITY_FAIL, payload: getErrorMessage(error) });
  }
};

// ================== ADMIN USER MGMT ==================

// ALL USERS
export const getAllUsers = () => async (dispatch) => {
  try {
    dispatch({ type: ALL_USERS_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.get(`${API_URL}/getAllUsers`, config);
    dispatch({ type: ALL_USERS_SUCCESS, payload: data.data || data.users });
  } catch (error) {
    dispatch({ type: ALL_USERS_FAIL, payload: getErrorMessage(error) });
  }
};

// USERS BY FRANCHISE
export const getUsersByFranchise = (franchiseId) => async (dispatch) => {
  try {
    dispatch({ type: USERS_BY_FRANCHISE_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.get(`${API_URL}/getUsersByFranchise/${franchiseId}`, config);
    dispatch({ type: USERS_BY_FRANCHISE_SUCCESS, payload: data.data || data.users });
  } catch (error) {
    dispatch({ type: USERS_BY_FRANCHISE_FAIL, payload: getErrorMessage(error) });
  }
};

// CREATE USER (Admin)
export const createUser = (userData) => async (dispatch) => {
  try {
    dispatch({ type: CREATE_USER_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.post(`${API_URL}/createUser`, userData, config);
    dispatch({ type: CREATE_USER_SUCCESS, payload: data.message });
  } catch (error) {
    dispatch({ type: CREATE_USER_FAIL, payload: getErrorMessage(error) });
  }
};

// EDIT USER (Admin)
export const editUser = (id, userData) => async (dispatch) => {
  try {
    dispatch({ type: EDIT_USER_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.put(`${API_URL}/editUser/${id}`, userData, config);
    dispatch({ type: EDIT_USER_SUCCESS, payload: data.message });
  } catch (error) {
    dispatch({ type: EDIT_USER_FAIL, payload: getErrorMessage(error) });
  }
};

// DELETE USER (Admin)
export const deleteUser = (id) => async (dispatch) => {
  try {
    dispatch({ type: DELETE_USER_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.delete(`${API_URL}/deleteUser/${id}`, config);
    dispatch({ type: DELETE_USER_SUCCESS, payload: data });
  } catch (error) {
    dispatch({ type: DELETE_USER_FAIL, payload: getErrorMessage(error) });
  }
};

// CHANGE USER ROLE
export const changeUserRole = (id, role) => async (dispatch) => {
  try {
    dispatch({ type: CHANGE_USER_ROLE_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.put(`${API_URL}/changeUserRole/${id}`, { role }, config);
    dispatch({ type: CHANGE_USER_ROLE_SUCCESS, payload: data.message });
  } catch (error) {
    dispatch({ type: CHANGE_USER_ROLE_FAIL, payload: getErrorMessage(error) });
  }
};

// TOGGLE USER STATUS
export const toggleUserStatus = (id) => async (dispatch) => {
  try {
    dispatch({ type: TOGGLE_USER_STATUS_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.put(`${API_URL}/toggleUserStatus/${id}`, {}, config);
    dispatch({ type: TOGGLE_USER_STATUS_SUCCESS, payload: data.message });
  } catch (error) {
    dispatch({ type: TOGGLE_USER_STATUS_FAIL, payload: getErrorMessage(error) });
  }
};

// ================== FRANCHISE MGMT ==================

// CREATE FRANCHISE
export const createFranchise = (franchiseData) => async (dispatch) => {
  try {
    dispatch({ type: CREATE_FRANCHISE_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.post(`${API_URL}/createFranchise`, franchiseData, config);
    dispatch({ type: CREATE_FRANCHISE_SUCCESS, payload: data });
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || "Error creating franchise.";
    dispatch({ type: CREATE_FRANCHISE_FAIL, payload: msg });
  }
};

// GET ALL FRANCHISES
export const getAllFranchises = () => async (dispatch) => {
  try {
    dispatch({ type: GET_ALL_FRANCHISES_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.get(`${API_URL}/getAllFranchises`, config);
    dispatch({ type: GET_ALL_FRANCHISES_SUCCESS, payload: data.franchises || data.data });
  } catch (error) {
    dispatch({ type: GET_ALL_FRANCHISES_FAIL, payload: getErrorMessage(error) });
  }
};

// SELECT FRANCHISE with optional callback
export const selectFranchise = (franchiseId, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: SELECT_FRANCHISE_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.post(
      `${API_URL}/selectFranchise`,
      { franchiseId },
      config
    );
    dispatch({
      type: SELECT_FRANCHISE_SUCCESS,
      payload: data.franchise || data,
    });
    if (onSuccess) onSuccess(data.franchise || data); // Call callback if passed
  } catch (error) {
    dispatch({ type: SELECT_FRANCHISE_FAIL, payload: getErrorMessage(error) });
  }
};

// SET FRANCHISE CONSOLE
export const setFranchiseConsole = (franchiseId, consoleType) => async (dispatch) => {
  try {
    dispatch({ type: SET_FRANCHISE_CONSOLE_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.post(`${API_URL}/setFranchiseConsole`, { franchiseId, console: consoleType }, config);
    dispatch({ type: SET_FRANCHISE_CONSOLE_SUCCESS, payload: data });
  } catch (error) {
    dispatch({ type: SET_FRANCHISE_CONSOLE_FAIL, payload: getErrorMessage(error) });
  }
};

// GET ACCESSIBLE FRANCHISES
export const getAccessibleFranchises = () => async (dispatch) => {
  try {
    dispatch({ type: GET_ACCESSIBLE_FRANCHISES_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.get(`${API_URL}/accessibleFranchises`, config);
    dispatch({ type: GET_ACCESSIBLE_FRANCHISES_SUCCESS, payload: data.franchises || data.data });
  } catch (error) {
    dispatch({ type: GET_ACCESSIBLE_FRANCHISES_FAIL, payload: getErrorMessage(error) });
  }
};

// ================== FRANCHISE STOCK/SALES ==================

// GET FRANCHISE STOCK
export const getFranchiseStock = (franchiseId) => async (dispatch) => {
  try {
    dispatch({ type: GET_FRANCHISE_STOCK_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.get(`${API_URL}/franchise/${franchiseId}/stock`, config);
    dispatch({ type: GET_FRANCHISE_STOCK_SUCCESS, payload: data.stock });
  } catch (error) {
    dispatch({ type: GET_FRANCHISE_STOCK_FAIL, payload: getErrorMessage(error) });
  }
};

// GET FRANCHISE SALES
export const getFranchiseSales = (franchiseId) => async (dispatch) => {
  try {
    dispatch({ type: GET_FRANCHISE_SALES_REQUEST });
    const config = getAuthConfig();
    const { data } = await axios.get(`${API_URL}/franchise/${franchiseId}/sales`, config);
    dispatch({ type: GET_FRANCHISE_SALES_SUCCESS, payload: data.sales });
  } catch (error) {
    dispatch({ type: GET_FRANCHISE_SALES_FAIL, payload: getErrorMessage(error) });
  }
};

// ================== CLEAR ERRORS ==================
export const clearErrors = () => (dispatch) => {
  dispatch({ type: CLEAR_ERRORS });
};
